// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ============================================
   BOSS MAP MODAL — bespoke map-viewport modal
   for boss encounters. Intentionally minimal:
   backdrop + panel + close X only. BatchBossSession's
   own chrome (combat header/HP bars/question area)
   does the work inside, on a crimson accent.
   ============================================ */

.bmm-overlay {
  position: absolute;
  inset: 0;
  z-index: 100;
  container-type: inline-size;
  background: rgba(6, 4, 8, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  padding: 28px;
  animation: bmm-scrim-fade-in 0.16s ease-out;
}

@keyframes bmm-scrim-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Backdrop-filter flicker fix (Edward 2026-06-20): the combat modal's blur(4px)
   scrim sits over the live dashboard, where the orb tray spins and nav grids
   shimmer on infinite CSS animations. A backdrop-filter over perpetually-
   repainting content makes Chrome re-rasterize the blur every frame and
   intermittently DROP it for a frame, flashing the sharp drug-lab map through
   the modal ("the combat modal flickers, revealing the map behind"). Pause every
   animation OUTSIDE the modal while it is open: blurred-and-paused is visually
   identical (you can't perceive rotation through a 4px blur), and a static
   backdrop lets Chrome cache the blur layer so it never re-rasters. The modal's
   own animations (.bmm-overlay subtree) keep running. The below-map concept
   carousel is outside the scrim, so its track is exempt. */
body:has(.bmm-overlay) *:not(.bmm-overlay):not(.bmm-overlay *):not(.cards-carousel-track) {
  animation-play-state: paused !important;
}

.bmm-body {
  --bmm-hud-close-reserve: 56px;
  --bmm-hud-height: 72px;
  --bmm-hp-side-width: 220px;
  --bmm-session-pad-x: 24px;
  --bmm-question-pad-x: 2rem;
  position: relative;
  width: 100%;
  height: 100%;
  background: rgba(10, 10, 14, 0.96);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(239, 68, 68, 0.16);
  border-radius: 10px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(239, 68, 68, 0.05) inset;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: bmm-pop-in 0.22s cubic-bezier(0.34, 1.2, 0.5, 1);
}

@keyframes bmm-pop-in {
  from { opacity: 0; transform: scale(0.98); }
  to   { opacity: 1; transform: scale(1); }
}

.bmm-close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.26);
  border-radius: 8px;
  color: rgba(248, 113, 113, 0.9);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.bmm-close:hover {
  background: rgba(239, 68, 68, 0.22);
  border-color: rgba(239, 68, 68, 0.52);
  color: #fff;
}

/* ── Modal-owned header (mirrors .mcp-header in MapCombatPanel) ──
   The boss session inside .bmm-content has its own .combat-boss-info
   hidden so the modal owns the title chrome (avatar + name + trait).
   This keeps the close X uncovered (no sticky competitor) and matches
   the canonical learn-node modal pattern.

   Visually merges with the .combat-header HP-bars sub-strip below it
   into one cohesive modal-top region — same whisper-translucent bg,
   no separator between them. */
.bmm-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(239, 68, 68, 0.12);
  flex-shrink: 0;
  /* leave space on the right for the absolute-positioned combat-header
     (HP pills + streak) and the close X */
  padding-right: var(--bmm-hud-close-reserve);
  min-height: var(--bmm-hud-height);
}

.bmm-avatar {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--creature-color, #ef4444);
  filter: drop-shadow(0 0 8px color-mix(in srgb, var(--creature-color, #ef4444) 45%, transparent));
  flex-shrink: 0;
}

.bmm-avatar svg {
  width: 100%;
  height: 100%;
}

.bmm-name-col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 0 1 460px;
}

.bmm-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--creature-color, #ef4444);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 360px;
  text-shadow: 0 0 8px color-mix(in srgb, var(--creature-color, #ef4444) 25%, transparent);
}

.bmm-trait {
  font-size: 11px;
  color: rgba(220, 220, 230, 0.65);
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.bmm-ability-summary {
  align-self: center;
  width: 100%;
  max-width: 460px;
  color: rgba(220, 220, 230, 0.48);
  font-size: 11px;
  font-style: italic;
  line-height: 1.25;
  text-align: center;
}

.bmm-content {
  flex: 1 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: visible; /* combat-header escapes upward to anchor against .bmm-body */
}

/* The boss-session / batch-boss IS the whole session (combat-header +
   HP bars + question body), not a question card centered for reading
   width. Override the modal-variants.css global cap that pins direct
   children of .bmm-content to 920px — boss/batch-boss fills the full
   panel width so the combat-header reads modal-wide like .mcp-header
   does in MapCombatPanel. */
.bmm-content > .boss-session,
.bmm-content > .batch-boss {
  width: 100%;
  max-width: 100%;
  margin: 0;
  align-self: stretch;
}

/* ── BatchBossSession embedded inside the modal ──
   The full-page variant sets .batch-boss { min-height: 100vh } + its
   own bg, and .batch-boss-container caps max-width + margins auto.
   Collapse both so the combat chrome fills the modal body. Keep the
   sticky combat header + HP bars + question area intact — that's the
   point of the modal. */
.bmm-content .batch-boss,
.bmm-content .batch-boss--damage-flash,
.bmm-content .batch-boss--heal-flash {
  min-height: 0;
  height: 100%;
  flex: 1 1;
  display: flex;
  flex-direction: column;
  background: transparent;
  border: none;
  overflow: hidden;
}

.bmm-content .batch-boss-container {
  max-width: none;
  margin: 0;
  /* Match .mcp-content rhythm + .ls-card-viewport vertical centering
     so the gauntlet/batch question card sits in the same canonical
     position as drill/trial/content cards in regular learn-node modals. */
  padding: 8px var(--bmm-session-pad-x) 20px;
  flex: 1 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: 16px;
}

.bmm-content .batch-session-chrome {
  flex: 1 1;
  min-height: 0;
  height: 100%;
  padding: 0;
  --sc-app-chrome-h: 0px;
  --sc-panel-max-h: 100%;
  --sc-safe-pad: 0px;
}

.bmm-content .batch-session-chrome .session-chrome__panel {
  width: 100%;
  height: 100%;
  max-height: 100%;
  border: 0;
  box-shadow: none;
}

.bmm-content .batch-session-chrome .batch-question-area {
  max-width: none;
  width: 100%;
  margin: 0;
  padding: 0;
}

/* Combat header stays sticky inside the modal body, aligns to the
   modal top-padding. Background is transparent so the chip row matches
   the rest of the modal surface; SessionChrome-2026-04 owns its own
   close X row above this header, so the chip row no longer collides
   with the bmm-close button — round chip sits flush with the row's
   right edge for left/right symmetry against the name chip. */
.bmm-content .batch-combat-header {
  padding: var(--space-3) var(--space-4);
  background: transparent;
  border-bottom: 1px solid rgba(239, 68, 68, 0.1);
}

/* Enemy-header dark backing — parity fix (Edward 2026-07-23). The combat header
   is the structural mirror of the player-bar-stack (CombatPlayerbar.css), but it
   was left \`background: transparent\` while the player stack carries a dark
   gradient. So the player status rail composited over dark (~rgb 13,13,17) while
   the enemy status rail — near-transparent itself — let the .session-chrome__panel
   light-gray (rgba .024) bleed through the transparent header and rendered a
   LIGHTER gray (~rgb 18,18,21): the "gray artifact" the enemy rail showed and the
   player rail didn't. Give the header the SAME dark gradient the player stack uses
   so the enemy rail composites over the identical dark layer (measured to parity).
   Scoped to the HTN-regimen pool (the antihypertensives standalone, this preprod
   batch) so glucose/athero/anaphyl/dialysis headers are untouched.

   Also kill the "black area BEHIND the enemy rail" (Edward 2026-07-23): the
   header's 12px \`padding-bottom\` (below the rail) is NOT covered by the rail's own
   +0.016 overlay, so under the dark backing it renders a few shades DARKER than the
   rail itself — a trailing black band below the rail that made the enemy status
   rail read taller and "reach further down" than the player rail (which is sized
   tight to its tags). Drop ONLY the bottom padding so the dark header ends flush at
   the rail, like the player-bar-stack ends flush at the player rail. KEEP the 8px
   top \`gap\` (between the boss HP row and the rail) — removing it too crammed the
   rail against the boss row ("too short"); the gap blends with the boss-row dark
   area above and reads as breathing room, not a trailing black band. */
.bmm-body[data-pool="regimen"] .bmm-content .batch-combat-header {
  background: linear-gradient(180deg, rgba(10, 10, 14, 0.92) 0%, rgba(10, 10, 14, 0.96) 100%);
  padding-bottom: 0;
}

/* Enemy-rail edge-to-edge FLUSH fix (Edward 2026-07-23): the base rail breakout
   (CombatPlayerbar.css) hardcodes \`margin: 0 -14px\` to escape the header's
   horizontal padding so the rail (and its top separator line) spans edge-to-edge
   like the player rail. But under .bmm-content the header padding is
   var(--space-4) = 16px (BossMapModal.css override above), NOT 14px — so a -14px
   breakout stops 2px short of the panel edge on each side: the enemy separator
   reads INSET while the player rail's separator sits flush. Break out by the
   header's ACTUAL padding so both separator lines land flush at the panel edges.
   Scoped to the HTN-regimen pool (this preprod batch). */
.bmm-body[data-pool="regimen"] .bmm-content .batch-combat-header > .combat-status-rail--enemy {
  margin-left: calc(-1 * var(--space-4));
  margin-right: calc(-1 * var(--space-4));
  width: calc(100% + 2 * var(--space-4));
}

/* Kill the orange left-edge line on the combat chrome (Edward 2026-07-23).
   ISOLATED empirically: \`.batch-announce { display:none }\` removes it. On the
   ANNOUNCE / "Incoming attack" forewarning screen (phase=ANNOUNCE), the boss's
   decode card renders OUTRIGHT as a \`standalone\` \`CombatTooltipPanel\`
   (BatchBossSession.jsx:3381) — a near-black slab (\`#0b0e14\`) that vanishes
   against the dark combat chrome, so the ONLY visible part is its full-height 2px
   accent stripe (\`.combat-tt__panel::before\`). For Ten Shin that stripe is the
   deck-tier ORANGE (#f97316), pinned at the card's left edge which sits at the
   chrome's inner-left → a stray orange vertical line down the forewarning region.
   Suppress the accent stripe on the standalone announce card in the HTN-regimen
   combat (the card's text/decode content is unaffected; real hover tooltips and
   the other pools — excluded by data-pool=regimen — keep their stripe). */
.bmm-body[data-pool="regimen"] .batch-announce .combat-tt__panel--standalone::before {
  content: none;
}

/* Hide BatchBossSession's own back button — the modal's close X owns exit. */
.bmm-content .batch-combat-back {
  display: none;
}

/* ── BossSession (v1, used by Apothecarius diagnostic combat) ──
   Mirrors the .batch-* scoping above. BossSession.css sets
   .boss-session { min-height: 100vh } + its own bg, and
   .boss-container caps max-width + margins auto — both need to
   collapse inside the modal body so the combat chrome fits the
   panel instead of being a full page rammed into it.
   Same canonical pattern as MapCombatPanel.css uses for .ls-page. */
.bmm-content .boss-session,
.bmm-content .boss-session--attacked {
  min-height: 0;
  height: 100%;
  flex: 1 1;
  display: flex;
  flex-direction: column;
  background: transparent;
  border: none;
  overflow: hidden;
}

.bmm-content .boss-container,
.bmm-content .boss-container--wide {
  max-width: none;
  margin: 0;
  /* Match .mcp-content rhythm from regular learn-node modals
     (MapCombatPanel.css) so the boss session reads with the same outer
     padding as drill/trial/content sessions. */
  padding: 8px var(--bmm-session-pad-x) 20px;
  flex: 1 1;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  /* Canonical learn-node pattern (.ls-card-viewport in LevelSession.css)
     vertically centers the card. Mirror it inside the boss modal. */
  justify-content: center;
  align-items: stretch;
  gap: 16px;
}

/* Cap the boss question card width and center horizontally to match
   .ls-card-wrapper from regular learn-node sessions. Same design token,
   same padding rhythm — boss modal reads with the same single-column
   focused-drill layout users see in drill/trial/content modals.
   Covers both BossSession (.combat-block-wrapper) and BatchBossSession
   (.batch-question-area) inner question containers. */
.bmm-content .combat-block-wrapper,
.bmm-content .batch-question-area {
  max-width: var(--container-content);
  width: 100%;
  margin: 0 auto;
  padding: var(--space-6) var(--bmm-question-pad-x);
}

/* Inside the modal, the .bmm-header above owns the avatar + name + trait.
   Hide BossSession's own boss-info so the title isn't duplicated and
   .combat-header collapses to just the HP-bars sub-strip. */
.bmm-content .combat-boss-info {
  display: none;
}

/* combat-header (HP pills + streak + attack timer) absolutely anchored
   against .bmm-body so it sits IN-LINE with the modal-owned .bmm-header
   (avatar + name + trait) on the same row. .bmm-header reserves room
   on the right via padding-right; combat-header anchors top-right and
   floats above it. ONE cohesive top region, single horizontal HUD. */
.bmm-content .combat-header {
  position: absolute;
  top: 0;
  right: var(--bmm-hud-close-reserve); /* clear close X */
  height: var(--bmm-hud-height); /* match .bmm-header min-height so it aligns vertically */
  display: flex;
  align-items: center;
  padding: 0;
  background: transparent;
  border: none;
  z-index: 2;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

/* HP column inside the modal renders as a single tight horizontal row:
   YOU pill | BOSS pill | streak pill. No wrap — this is one HUD strip
   beneath the modal title, not a multi-line layout. */
.bmm-content .combat-hp-column {
  flex: 1 1;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.bmm-content .combat-hp-side {
  flex: 0 0 auto;
  min-width: 0;
  width: var(--bmm-hp-side-width);
}

/* The second .combat-hp-side slot is reused by BossSession to render a
   .gauntlet-section-header (e.g. "ACE INHIBITORS") when there's no boss
   HP pill (Apothecarius / gauntlet flows). Restyle that wrapper so the
   section name reads as a small section-context pill in the HUD strip,
   not as a wide hp-pill-shaped slot wedged between Your HP and streak. */
.bmm-content .combat-hp-side:has(.gauntlet-section-header) {
  width: auto;
  flex: 0 0 auto;
  margin-left: 4px;
}

.bmm-content .gauntlet-section-header {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  height: 22px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 8px;
  white-space: nowrap;
}

.bmm-content .gauntlet-section-title {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(220, 220, 230, 0.78);
  line-height: 1;
}

/* Inside the modal panel, the BLS-pill chrome from the dashboard rail
   reads too transparent against the modal's glassy bg. Bump the surface
   contrast so the pills actually look like chips, not floating text. */
.bmm-content .hp-pill,
.bmm-content .combat-streak {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.12);
}

/* Player HP is already on display in the BottomLeftStatusRail HUD —
   showing it again inside the modal HUD is redundant noise. Hide it
   AND collapse its hp-side wrapper so the streak chip + section pill
   anchor cleanly without an empty slot. */
.bmm-content .hp-pill--player {
  display: none;
}

.bmm-content .combat-hp-side:has(.hp-pill--player) {
  display: none;
}

.bmm-content .combat-streak--hot {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.22);
}

.bmm-content .combat-streak--blazing {
  background: rgba(239, 68, 68, 0.14);
  border-color: rgba(239, 68, 68, 0.32);
}

/* Status row holds the streak chip + survival-scale tag — slot it into
   the same row as the HP pills, right-aligned via auto-margin so it
   anchors at the trailing edge. */
.bmm-content .combat-status-row {
  flex: 0 0 auto;
  margin: 0 0 0 auto;
  gap: 6px;
  align-items: center;
}

/* Hide BossSession's own back button — the modal's close X owns exit. */
.bmm-content .combat-back {
  display: none;
}

/* Hide the UserActionBar/exam-tabs chrome that BatchBossSession mounts
   for the full-page route — the dashboard behind the modal already shows
   the same upgrade / bug-report / settings actions, and duplicating them
   inside the combat panel is redundant noise. */
.bmm-content .exam-tabs-bar {
  display: none;
}

/* Full-screen damage / heal flash overlays inside the modal would tint
   the dashboard behind it. Scope the flash to the modal body. */
.bmm-content .batch-self-damage-flash,
.bmm-content .batch-heal-flash {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

@container (max-width: 1800px) {
  .bmm-body {
    --bmm-hud-close-reserve: clamp(48px, 3.2cqi, 56px);
    --bmm-hud-height: clamp(64px, 4.1cqi, 72px);
    --bmm-hp-side-width: clamp(176px, 12.5cqi, 220px);
    --bmm-session-pad-x: clamp(16px, 1.35cqi, 24px);
    --bmm-question-pad-x: clamp(1rem, 1.8cqi, 2rem);
  }

  .bmm-content .combat-hp-column {
    gap: clamp(6px, 0.5cqi, 8px);
  }

  .bmm-content .combat-status-row {
    gap: clamp(4px, 0.4cqi, 6px);
  }
}

/* Fallback when portal target isn't a map viewport: become centered overlay. */
body > .bmm-overlay {
  position: fixed;
  padding: 40px;
}

@media (max-width: 640px) {
  .bmm-overlay { padding: 16px; }
  .bmm-content .batch-combat-header {
    padding-right: 48px;
  }
}

/* ── Athero (lipid) pool accent override ─────────────────────────────
   Lipid mini-boss encounters (Chylo / Veldra / Idol / Eldra / Lipo-Alpha)
   paint the modal in amber-gold instead of the default crimson combat
   palette. Slug-to-pool mapping happens in App.jsx; this stylesheet
   only handles the visual swap once data-pool="athero" lands on the
   .bmm-body element. Color anchor: #fbbf24 (matches PlayerLipidTag). */
.bmm-body[data-pool="athero"] {
  border-color: rgba(251, 191, 36, 0.18);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(251, 191, 36, 0.06) inset;
}

.bmm-body[data-pool="athero"] .bmm-close {
  background: rgba(251, 191, 36, 0.1);
  border-color: rgba(251, 191, 36, 0.28);
  color: rgba(252, 211, 77, 0.95);
}
.bmm-body[data-pool="athero"] .bmm-close:hover {
  background: rgba(251, 191, 36, 0.22);
  border-color: rgba(251, 191, 36, 0.55);
  color: #fff;
}

.bmm-body[data-pool="athero"] .bmm-header {
  border-bottom-color: rgba(251, 191, 36, 0.14);
}

.bmm-body[data-pool="athero"] .bmm-avatar {
  color: #fbbf24;
  filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.45));
}

.bmm-body[data-pool="athero"] .bmm-name {
  color: #fef3c7;
}

.bmm-body[data-pool="athero"] .bmm-content .batch-combat-header {
  border-bottom-color: rgba(251, 191, 36, 0.12);
}

/* ── Anaphyl (MAB immunogenicity) pool accent override ───────────────
   Murin-OMAB and later anaphyl enemies use amber-red: #fbbf24 warm
   shoulder into #dc2626 core. Mirrors the athero override selectors. */
.bmm-body[data-pool="anaphyl"] {
  border-color: rgba(220, 38, 38, 0.22);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(251, 191, 36, 0.05) inset,
    0 0 32px rgba(220, 38, 38, 0.08);
}

.bmm-body[data-pool="anaphyl"] .bmm-close {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.10), rgba(220, 38, 38, 0.12));
  border-color: rgba(220, 38, 38, 0.34);
  color: rgba(248, 113, 113, 0.96);
}
.bmm-body[data-pool="anaphyl"] .bmm-close:hover {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.18), rgba(220, 38, 38, 0.24));
  border-color: rgba(220, 38, 38, 0.58);
  color: #fff;
}

.bmm-body[data-pool="anaphyl"] .bmm-header {
  border-bottom-color: rgba(220, 38, 38, 0.16);
}

.bmm-body[data-pool="anaphyl"] .bmm-avatar {
  color: #dc2626;
  filter: drop-shadow(0 0 8px rgba(220, 38, 38, 0.46));
}

.bmm-body[data-pool="anaphyl"] .bmm-name {
  color: #fee2e2;
  text-shadow: 0 0 10px rgba(251, 191, 36, 0.16);
}

.bmm-body[data-pool="anaphyl"] .bmm-content .batch-combat-header {
  border-bottom-color: rgba(220, 38, 38, 0.14);
}

/* ── Dialysis pool accent override ───────────────────────────────────
   Dialysis encounters reuse BatchBossSession with semantic inversion
   (boss HP = toxin pool the player drains by answering correctly).
   Cyan/teal palette to distinguish from combat-red. Anchor: #06b6d4
   (cyan-500); deep stop #0891b2 (cyan-700). Mirrors the athero/anaphyl
   selector structure. */
.bmm-body[data-pool="dialysis"] {
  border-color: rgba(6, 182, 212, 0.18);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(6, 182, 212, 0.06) inset;
}

.bmm-body[data-pool="dialysis"] .bmm-close {
  background: rgba(6, 182, 212, 0.1);
  border-color: rgba(6, 182, 212, 0.28);
  color: rgba(34, 211, 238, 0.95);
}
.bmm-body[data-pool="dialysis"] .bmm-close:hover {
  background: rgba(6, 182, 212, 0.22);
  border-color: rgba(6, 182, 212, 0.55);
  color: #fff;
}

.bmm-body[data-pool="dialysis"] .bmm-content .batch-combat-header {
  border-bottom-color: rgba(6, 182, 212, 0.14);
}
`, "",{"version":3,"sources":["webpack://./src/components/BossMapModal/BossMapModal.css"],"names":[],"mappings":"AAAA;;;;;;iDAMiD;;AAEjD;EACE,kBAAkB;EAClB,QAAQ;EACR,YAAY;EACZ,2BAA2B;EAC3B,+BAA+B;EAC/B,0BAA0B;EAC1B,kCAAkC;EAClC,aAAa;EACb,oBAAoB;EACpB,wBAAwB;EACxB,aAAa;EACb,2CAA2C;AAC7C;;AAEA;EACE,OAAO,UAAU,EAAE;EACnB,KAAK,UAAU,EAAE;AACnB;;AAEA;;;;;;;;;;2DAU2D;AAC3D;EACE,uCAAuC;AACzC;;AAEA;EACE,6BAA6B;EAC7B,sBAAsB;EACtB,0BAA0B;EAC1B,yBAAyB;EACzB,0BAA0B;EAC1B,kBAAkB;EAClB,WAAW;EACX,YAAY;EACZ,kCAAkC;EAClC,2BAA2B;EAC3B,mCAAmC;EACnC,yCAAyC;EACzC,mBAAmB;EACnB;;2CAEyC;EACzC,gBAAgB;EAChB,aAAa;EACb,sBAAsB;EACtB,2DAA2D;AAC7D;;AAEA;EACE,OAAO,UAAU,EAAE,sBAAsB,EAAE;EAC3C,OAAO,UAAU,EAAE,mBAAmB,EAAE;AAC1C;;AAEA;EACE,kBAAkB;EAClB,SAAS;EACT,WAAW;EACX,UAAU;EACV,WAAW;EACX,YAAY;EACZ,UAAU;EACV,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,kCAAkC;EAClC,yCAAyC;EACzC,kBAAkB;EAClB,+BAA+B;EAC/B,eAAe;EACf,4EAA4E;AAC9E;;AAEA;EACE,mCAAmC;EACnC,qCAAqC;EACrC,WAAW;AACb;;AAEA;;;;;;;;+BAQ+B;AAC/B;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,kBAAkB;EAClB,qCAAqC;EACrC,gDAAgD;EAChD,cAAc;EACd;0CACwC;EACxC,2CAA2C;EAC3C,iCAAiC;AACnC;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,qCAAqC;EACrC,gGAAgG;EAChG,cAAc;AAChB;;AAEA;EACE,WAAW;EACX,YAAY;AACd;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,YAAY;EACZ,eAAe;AACjB;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,qCAAqC;EACrC,yBAAyB;EACzB,sBAAsB;EACtB,gBAAgB;EAChB,mBAAmB;EACnB,gBAAgB;EAChB,uBAAuB;EACvB,gBAAgB;EAChB,wFAAwF;AAC1F;;AAEA;EACE,eAAe;EACf,gCAAgC;EAChC,cAAc;EACd,yBAAyB;EACzB,sBAAsB;AACxB;;AAEA;EACE,kBAAkB;EAClB,WAAW;EACX,gBAAgB;EAChB,gCAAgC;EAChC,eAAe;EACf,kBAAkB;EAClB,iBAAiB;EACjB,kBAAkB;AACpB;;AAEA;EACE,SAAO;EACP,aAAa;EACb,aAAa;EACb,sBAAsB;EACtB,iBAAiB,EAAE,6DAA6D;AAClF;;AAEA;;;;;4BAK4B;AAC5B;;EAEE,WAAW;EACX,eAAe;EACf,SAAS;EACT,mBAAmB;AACrB;;AAEA;;;;;wBAKwB;AACxB;;;EAGE,aAAa;EACb,YAAY;EACZ,SAAO;EACP,aAAa;EACb,sBAAsB;EACtB,uBAAuB;EACvB,YAAY;EACZ,gBAAgB;AAClB;;AAEA;EACE,eAAe;EACf,SAAS;EACT;;0EAEwE;EACxE,0CAA0C;EAC1C,SAAO;EACP,aAAa;EACb,gBAAgB;EAChB,aAAa;EACb,sBAAsB;EACtB,uBAAuB;EACvB,oBAAoB;EACpB,SAAS;AACX;;AAEA;EACE,SAAO;EACP,aAAa;EACb,YAAY;EACZ,UAAU;EACV,sBAAsB;EACtB,sBAAsB;EACtB,kBAAkB;AACpB;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,gBAAgB;EAChB,SAAS;EACT,gBAAgB;AAClB;;AAEA;EACE,eAAe;EACf,WAAW;EACX,SAAS;EACT,UAAU;AACZ;;AAEA;;;;;8DAK8D;AAC9D;EACE,sCAAsC;EACtC,uBAAuB;EACvB,+CAA+C;AACjD;;AAEA;;;;;;;;;;;;;;;;;;;;;uEAqBuE;AACvE;EACE,2FAA2F;EAC3F,iBAAiB;AACnB;;AAEA;;;;;;;;yDAQyD;AACzD;EACE,sCAAsC;EACtC,uCAAuC;EACvC,sCAAsC;AACxC;;AAEA;;;;;;;;;;;0EAW0E;AAC1E;EACE,aAAa;AACf;;AAEA,6EAA6E;AAC7E;EACE,aAAa;AACf;;AAEA;;;;;;oEAMoE;AACpE;;EAEE,aAAa;EACb,YAAY;EACZ,SAAO;EACP,aAAa;EACb,sBAAsB;EACtB,uBAAuB;EACvB,YAAY;EACZ,gBAAgB;AAClB;;AAEA;;EAEE,eAAe;EACf,SAAS;EACT;;+CAE6C;EAC7C,0CAA0C;EAC1C,SAAO;EACP,aAAa;EACb,gBAAgB;EAChB,WAAW;EACX,aAAa;EACb,sBAAsB;EACtB;oEACkE;EAClE,uBAAuB;EACvB,oBAAoB;EACpB,SAAS;AACX;;AAEA;;;;;sDAKsD;AACtD;;EAEE,mCAAmC;EACnC,WAAW;EACX,cAAc;EACd,iDAAiD;AACnD;;AAEA;;4DAE4D;AAC5D;EACE,aAAa;AACf;;AAEA;;;;qEAIqE;AACrE;EACE,kBAAkB;EAClB,MAAM;EACN,mCAAmC,EAAE,kBAAkB;EACvD,6BAA6B,EAAE,yDAAyD;EACxF,aAAa;EACb,mBAAmB;EACnB,UAAU;EACV,uBAAuB;EACvB,YAAY;EACZ,UAAU;EACV,qBAAqB;EACrB,6BAA6B;AAC/B;;AAEA;;sDAEsD;AACtD;EACE,SAAO;EACP,aAAa;EACb,mBAAmB;EACnB,iBAAiB;EACjB,QAAQ;EACR,mBAAmB;EACnB,YAAY;AACd;;AAEA;EACE,cAAc;EACd,YAAY;EACZ,+BAA+B;AACjC;;AAEA;;;;yEAIyE;AACzE;EACE,WAAW;EACX,cAAc;EACd,gBAAgB;AAClB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,iBAAiB;EACjB,YAAY;EACZ,qCAAqC;EACrC,2CAA2C;EAC3C,kBAAkB;EAClB,mBAAmB;AACrB;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,gCAAgC;EAChC,cAAc;AAChB;;AAEA;;uEAEuE;AACvE;;EAEE,qCAAqC;EACrC,uCAAuC;AACzC;;AAEA;;;0CAG0C;AAC1C;EACE,aAAa;AACf;;AAEA;EACE,aAAa;AACf;;AAEA;EACE,mCAAmC;EACnC,qCAAqC;AACvC;;AAEA;EACE,mCAAmC;EACnC,qCAAqC;AACvC;;AAEA;;kCAEkC;AAClC;EACE,cAAc;EACd,kBAAkB;EAClB,QAAQ;EACR,mBAAmB;AACrB;;AAEA,wEAAwE;AACxE;EACE,aAAa;AACf;;AAEA;;;gDAGgD;AAChD;EACE,aAAa;AACf;;AAEA;gEACgE;AAChE;;EAEE,kBAAkB;EAClB,QAAQ;EACR,oBAAoB;AACtB;;AAEA;EACE;IACE,kDAAkD;IAClD,2CAA2C;IAC3C,iDAAiD;IACjD,+CAA+C;IAC/C,+CAA+C;EACjD;;EAEA;IACE,4BAA4B;EAC9B;;EAEA;IACE,4BAA4B;EAC9B;AACF;;AAEA,+EAA+E;AAC/E;EACE,eAAe;EACf,aAAa;AACf;;AAEA;EACE,eAAe,aAAa,EAAE;EAC9B;IACE,mBAAmB;EACrB;AACF;;AAEA;;;;;uEAKuE;AACvE;EACE,sCAAsC;EACtC;;4CAE0C;AAC5C;;AAEA;EACE,mCAAmC;EACnC,sCAAsC;EACtC,+BAA+B;AACjC;AACA;EACE,oCAAoC;EACpC,sCAAsC;EACtC,WAAW;AACb;;AAEA;EACE,6CAA6C;AAC/C;;AAEA;EACE,cAAc;EACd,qDAAqD;AACvD;;AAEA;EACE,cAAc;AAChB;;AAEA;EACE,6CAA6C;AAC/C;;AAEA;;uEAEuE;AACvE;EACE,qCAAqC;EACrC;;;oCAGkC;AACpC;;AAEA;EACE,sFAAsF;EACtF,qCAAqC;EACrC,gCAAgC;AAClC;AACA;EACE,sFAAsF;EACtF,qCAAqC;EACrC,WAAW;AACb;;AAEA;EACE,4CAA4C;AAC9C;;AAEA;EACE,cAAc;EACd,oDAAoD;AACtD;;AAEA;EACE,cAAc;EACd,8CAA8C;AAChD;;AAEA;EACE,4CAA4C;AAC9C;;AAEA;;;;;wBAKwB;AACxB;EACE,qCAAqC;EACrC;;2CAEyC;AAC3C;;AAEA;EACE,kCAAkC;EAClC,qCAAqC;EACrC,+BAA+B;AACjC;AACA;EACE,mCAAmC;EACnC,qCAAqC;EACrC,WAAW;AACb;;AAEA;EACE,4CAA4C;AAC9C","sourcesContent":["/* ============================================\n   BOSS MAP MODAL — bespoke map-viewport modal\n   for boss encounters. Intentionally minimal:\n   backdrop + panel + close X only. BatchBossSession's\n   own chrome (combat header/HP bars/question area)\n   does the work inside, on a crimson accent.\n   ============================================ */\n\n.bmm-overlay {\n  position: absolute;\n  inset: 0;\n  z-index: 100;\n  container-type: inline-size;\n  background: rgba(6, 4, 8, 0.55);\n  backdrop-filter: blur(4px);\n  -webkit-backdrop-filter: blur(4px);\n  display: flex;\n  align-items: stretch;\n  justify-content: stretch;\n  padding: 28px;\n  animation: bmm-scrim-fade-in 0.16s ease-out;\n}\n\n@keyframes bmm-scrim-fade-in {\n  from { opacity: 0; }\n  to { opacity: 1; }\n}\n\n/* Backdrop-filter flicker fix (Edward 2026-06-20): the combat modal's blur(4px)\n   scrim sits over the live dashboard, where the orb tray spins and nav grids\n   shimmer on infinite CSS animations. A backdrop-filter over perpetually-\n   repainting content makes Chrome re-rasterize the blur every frame and\n   intermittently DROP it for a frame, flashing the sharp drug-lab map through\n   the modal (\"the combat modal flickers, revealing the map behind\"). Pause every\n   animation OUTSIDE the modal while it is open: blurred-and-paused is visually\n   identical (you can't perceive rotation through a 4px blur), and a static\n   backdrop lets Chrome cache the blur layer so it never re-rasters. The modal's\n   own animations (.bmm-overlay subtree) keep running. The below-map concept\n   carousel is outside the scrim, so its track is exempt. */\nbody:has(.bmm-overlay) *:not(.bmm-overlay):not(.bmm-overlay *):not(.cards-carousel-track) {\n  animation-play-state: paused !important;\n}\n\n.bmm-body {\n  --bmm-hud-close-reserve: 56px;\n  --bmm-hud-height: 72px;\n  --bmm-hp-side-width: 220px;\n  --bmm-session-pad-x: 24px;\n  --bmm-question-pad-x: 2rem;\n  position: relative;\n  width: 100%;\n  height: 100%;\n  background: rgba(10, 10, 14, 0.96);\n  backdrop-filter: blur(14px);\n  -webkit-backdrop-filter: blur(14px);\n  border: 1px solid rgba(239, 68, 68, 0.16);\n  border-radius: 10px;\n  box-shadow:\n    0 8px 32px rgba(0, 0, 0, 0.6),\n    0 0 0 1px rgba(239, 68, 68, 0.05) inset;\n  overflow: hidden;\n  display: flex;\n  flex-direction: column;\n  animation: bmm-pop-in 0.22s cubic-bezier(0.34, 1.2, 0.5, 1);\n}\n\n@keyframes bmm-pop-in {\n  from { opacity: 0; transform: scale(0.98); }\n  to   { opacity: 1; transform: scale(1); }\n}\n\n.bmm-close {\n  position: absolute;\n  top: 12px;\n  right: 12px;\n  z-index: 5;\n  width: 32px;\n  height: 32px;\n  padding: 0;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  background: rgba(239, 68, 68, 0.1);\n  border: 1px solid rgba(239, 68, 68, 0.26);\n  border-radius: 8px;\n  color: rgba(248, 113, 113, 0.9);\n  cursor: pointer;\n  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;\n}\n\n.bmm-close:hover {\n  background: rgba(239, 68, 68, 0.22);\n  border-color: rgba(239, 68, 68, 0.52);\n  color: #fff;\n}\n\n/* ── Modal-owned header (mirrors .mcp-header in MapCombatPanel) ──\n   The boss session inside .bmm-content has its own .combat-boss-info\n   hidden so the modal owns the title chrome (avatar + name + trait).\n   This keeps the close X uncovered (no sticky competitor) and matches\n   the canonical learn-node modal pattern.\n\n   Visually merges with the .combat-header HP-bars sub-strip below it\n   into one cohesive modal-top region — same whisper-translucent bg,\n   no separator between them. */\n.bmm-header {\n  display: flex;\n  align-items: center;\n  gap: 14px;\n  padding: 14px 18px;\n  background: rgba(255, 255, 255, 0.02);\n  border-bottom: 1px solid rgba(239, 68, 68, 0.12);\n  flex-shrink: 0;\n  /* leave space on the right for the absolute-positioned combat-header\n     (HP pills + streak) and the close X */\n  padding-right: var(--bmm-hud-close-reserve);\n  min-height: var(--bmm-hud-height);\n}\n\n.bmm-avatar {\n  width: 44px;\n  height: 44px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  color: var(--creature-color, #ef4444);\n  filter: drop-shadow(0 0 8px color-mix(in srgb, var(--creature-color, #ef4444) 45%, transparent));\n  flex-shrink: 0;\n}\n\n.bmm-avatar svg {\n  width: 100%;\n  height: 100%;\n}\n\n.bmm-name-col {\n  display: flex;\n  flex-direction: column;\n  gap: 2px;\n  min-width: 0;\n  flex: 0 1 460px;\n}\n\n.bmm-name {\n  font-size: 14px;\n  font-weight: 700;\n  color: var(--creature-color, #ef4444);\n  text-transform: uppercase;\n  letter-spacing: 0.08em;\n  line-height: 1.1;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  max-width: 360px;\n  text-shadow: 0 0 8px color-mix(in srgb, var(--creature-color, #ef4444) 25%, transparent);\n}\n\n.bmm-trait {\n  font-size: 11px;\n  color: rgba(220, 220, 230, 0.65);\n  line-height: 1;\n  text-transform: uppercase;\n  letter-spacing: 0.12em;\n}\n\n.bmm-ability-summary {\n  align-self: center;\n  width: 100%;\n  max-width: 460px;\n  color: rgba(220, 220, 230, 0.48);\n  font-size: 11px;\n  font-style: italic;\n  line-height: 1.25;\n  text-align: center;\n}\n\n.bmm-content {\n  flex: 1;\n  min-height: 0;\n  display: flex;\n  flex-direction: column;\n  overflow: visible; /* combat-header escapes upward to anchor against .bmm-body */\n}\n\n/* The boss-session / batch-boss IS the whole session (combat-header +\n   HP bars + question body), not a question card centered for reading\n   width. Override the modal-variants.css global cap that pins direct\n   children of .bmm-content to 920px — boss/batch-boss fills the full\n   panel width so the combat-header reads modal-wide like .mcp-header\n   does in MapCombatPanel. */\n.bmm-content > .boss-session,\n.bmm-content > .batch-boss {\n  width: 100%;\n  max-width: 100%;\n  margin: 0;\n  align-self: stretch;\n}\n\n/* ── BatchBossSession embedded inside the modal ──\n   The full-page variant sets .batch-boss { min-height: 100vh } + its\n   own bg, and .batch-boss-container caps max-width + margins auto.\n   Collapse both so the combat chrome fills the modal body. Keep the\n   sticky combat header + HP bars + question area intact — that's the\n   point of the modal. */\n.bmm-content .batch-boss,\n.bmm-content .batch-boss--damage-flash,\n.bmm-content .batch-boss--heal-flash {\n  min-height: 0;\n  height: 100%;\n  flex: 1;\n  display: flex;\n  flex-direction: column;\n  background: transparent;\n  border: none;\n  overflow: hidden;\n}\n\n.bmm-content .batch-boss-container {\n  max-width: none;\n  margin: 0;\n  /* Match .mcp-content rhythm + .ls-card-viewport vertical centering\n     so the gauntlet/batch question card sits in the same canonical\n     position as drill/trial/content cards in regular learn-node modals. */\n  padding: 8px var(--bmm-session-pad-x) 20px;\n  flex: 1;\n  min-height: 0;\n  overflow-y: auto;\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: stretch;\n  gap: 16px;\n}\n\n.bmm-content .batch-session-chrome {\n  flex: 1;\n  min-height: 0;\n  height: 100%;\n  padding: 0;\n  --sc-app-chrome-h: 0px;\n  --sc-panel-max-h: 100%;\n  --sc-safe-pad: 0px;\n}\n\n.bmm-content .batch-session-chrome .session-chrome__panel {\n  width: 100%;\n  height: 100%;\n  max-height: 100%;\n  border: 0;\n  box-shadow: none;\n}\n\n.bmm-content .batch-session-chrome .batch-question-area {\n  max-width: none;\n  width: 100%;\n  margin: 0;\n  padding: 0;\n}\n\n/* Combat header stays sticky inside the modal body, aligns to the\n   modal top-padding. Background is transparent so the chip row matches\n   the rest of the modal surface; SessionChrome-2026-04 owns its own\n   close X row above this header, so the chip row no longer collides\n   with the bmm-close button — round chip sits flush with the row's\n   right edge for left/right symmetry against the name chip. */\n.bmm-content .batch-combat-header {\n  padding: var(--space-3) var(--space-4);\n  background: transparent;\n  border-bottom: 1px solid rgba(239, 68, 68, 0.1);\n}\n\n/* Enemy-header dark backing — parity fix (Edward 2026-07-23). The combat header\n   is the structural mirror of the player-bar-stack (CombatPlayerbar.css), but it\n   was left `background: transparent` while the player stack carries a dark\n   gradient. So the player status rail composited over dark (~rgb 13,13,17) while\n   the enemy status rail — near-transparent itself — let the .session-chrome__panel\n   light-gray (rgba .024) bleed through the transparent header and rendered a\n   LIGHTER gray (~rgb 18,18,21): the \"gray artifact\" the enemy rail showed and the\n   player rail didn't. Give the header the SAME dark gradient the player stack uses\n   so the enemy rail composites over the identical dark layer (measured to parity).\n   Scoped to the HTN-regimen pool (the antihypertensives standalone, this preprod\n   batch) so glucose/athero/anaphyl/dialysis headers are untouched.\n\n   Also kill the \"black area BEHIND the enemy rail\" (Edward 2026-07-23): the\n   header's 12px `padding-bottom` (below the rail) is NOT covered by the rail's own\n   +0.016 overlay, so under the dark backing it renders a few shades DARKER than the\n   rail itself — a trailing black band below the rail that made the enemy status\n   rail read taller and \"reach further down\" than the player rail (which is sized\n   tight to its tags). Drop ONLY the bottom padding so the dark header ends flush at\n   the rail, like the player-bar-stack ends flush at the player rail. KEEP the 8px\n   top `gap` (between the boss HP row and the rail) — removing it too crammed the\n   rail against the boss row (\"too short\"); the gap blends with the boss-row dark\n   area above and reads as breathing room, not a trailing black band. */\n.bmm-body[data-pool=\"regimen\"] .bmm-content .batch-combat-header {\n  background: linear-gradient(180deg, rgba(10, 10, 14, 0.92) 0%, rgba(10, 10, 14, 0.96) 100%);\n  padding-bottom: 0;\n}\n\n/* Enemy-rail edge-to-edge FLUSH fix (Edward 2026-07-23): the base rail breakout\n   (CombatPlayerbar.css) hardcodes `margin: 0 -14px` to escape the header's\n   horizontal padding so the rail (and its top separator line) spans edge-to-edge\n   like the player rail. But under .bmm-content the header padding is\n   var(--space-4) = 16px (BossMapModal.css override above), NOT 14px — so a -14px\n   breakout stops 2px short of the panel edge on each side: the enemy separator\n   reads INSET while the player rail's separator sits flush. Break out by the\n   header's ACTUAL padding so both separator lines land flush at the panel edges.\n   Scoped to the HTN-regimen pool (this preprod batch). */\n.bmm-body[data-pool=\"regimen\"] .bmm-content .batch-combat-header > .combat-status-rail--enemy {\n  margin-left: calc(-1 * var(--space-4));\n  margin-right: calc(-1 * var(--space-4));\n  width: calc(100% + 2 * var(--space-4));\n}\n\n/* Kill the orange left-edge line on the combat chrome (Edward 2026-07-23).\n   ISOLATED empirically: `.batch-announce { display:none }` removes it. On the\n   ANNOUNCE / \"Incoming attack\" forewarning screen (phase=ANNOUNCE), the boss's\n   decode card renders OUTRIGHT as a `standalone` `CombatTooltipPanel`\n   (BatchBossSession.jsx:3381) — a near-black slab (`#0b0e14`) that vanishes\n   against the dark combat chrome, so the ONLY visible part is its full-height 2px\n   accent stripe (`.combat-tt__panel::before`). For Ten Shin that stripe is the\n   deck-tier ORANGE (#f97316), pinned at the card's left edge which sits at the\n   chrome's inner-left → a stray orange vertical line down the forewarning region.\n   Suppress the accent stripe on the standalone announce card in the HTN-regimen\n   combat (the card's text/decode content is unaffected; real hover tooltips and\n   the other pools — excluded by data-pool=regimen — keep their stripe). */\n.bmm-body[data-pool=\"regimen\"] .batch-announce .combat-tt__panel--standalone::before {\n  content: none;\n}\n\n/* Hide BatchBossSession's own back button — the modal's close X owns exit. */\n.bmm-content .batch-combat-back {\n  display: none;\n}\n\n/* ── BossSession (v1, used by Apothecarius diagnostic combat) ──\n   Mirrors the .batch-* scoping above. BossSession.css sets\n   .boss-session { min-height: 100vh } + its own bg, and\n   .boss-container caps max-width + margins auto — both need to\n   collapse inside the modal body so the combat chrome fits the\n   panel instead of being a full page rammed into it.\n   Same canonical pattern as MapCombatPanel.css uses for .ls-page. */\n.bmm-content .boss-session,\n.bmm-content .boss-session--attacked {\n  min-height: 0;\n  height: 100%;\n  flex: 1;\n  display: flex;\n  flex-direction: column;\n  background: transparent;\n  border: none;\n  overflow: hidden;\n}\n\n.bmm-content .boss-container,\n.bmm-content .boss-container--wide {\n  max-width: none;\n  margin: 0;\n  /* Match .mcp-content rhythm from regular learn-node modals\n     (MapCombatPanel.css) so the boss session reads with the same outer\n     padding as drill/trial/content sessions. */\n  padding: 8px var(--bmm-session-pad-x) 20px;\n  flex: 1;\n  min-height: 0;\n  overflow-y: auto;\n  width: 100%;\n  display: flex;\n  flex-direction: column;\n  /* Canonical learn-node pattern (.ls-card-viewport in LevelSession.css)\n     vertically centers the card. Mirror it inside the boss modal. */\n  justify-content: center;\n  align-items: stretch;\n  gap: 16px;\n}\n\n/* Cap the boss question card width and center horizontally to match\n   .ls-card-wrapper from regular learn-node sessions. Same design token,\n   same padding rhythm — boss modal reads with the same single-column\n   focused-drill layout users see in drill/trial/content modals.\n   Covers both BossSession (.combat-block-wrapper) and BatchBossSession\n   (.batch-question-area) inner question containers. */\n.bmm-content .combat-block-wrapper,\n.bmm-content .batch-question-area {\n  max-width: var(--container-content);\n  width: 100%;\n  margin: 0 auto;\n  padding: var(--space-6) var(--bmm-question-pad-x);\n}\n\n/* Inside the modal, the .bmm-header above owns the avatar + name + trait.\n   Hide BossSession's own boss-info so the title isn't duplicated and\n   .combat-header collapses to just the HP-bars sub-strip. */\n.bmm-content .combat-boss-info {\n  display: none;\n}\n\n/* combat-header (HP pills + streak + attack timer) absolutely anchored\n   against .bmm-body so it sits IN-LINE with the modal-owned .bmm-header\n   (avatar + name + trait) on the same row. .bmm-header reserves room\n   on the right via padding-right; combat-header anchors top-right and\n   floats above it. ONE cohesive top region, single horizontal HUD. */\n.bmm-content .combat-header {\n  position: absolute;\n  top: 0;\n  right: var(--bmm-hud-close-reserve); /* clear close X */\n  height: var(--bmm-hud-height); /* match .bmm-header min-height so it aligns vertically */\n  display: flex;\n  align-items: center;\n  padding: 0;\n  background: transparent;\n  border: none;\n  z-index: 2;\n  backdrop-filter: none;\n  -webkit-backdrop-filter: none;\n}\n\n/* HP column inside the modal renders as a single tight horizontal row:\n   YOU pill | BOSS pill | streak pill. No wrap — this is one HUD strip\n   beneath the modal title, not a multi-line layout. */\n.bmm-content .combat-hp-column {\n  flex: 1;\n  display: flex;\n  flex-direction: row;\n  flex-wrap: nowrap;\n  gap: 8px;\n  align-items: center;\n  min-width: 0;\n}\n\n.bmm-content .combat-hp-side {\n  flex: 0 0 auto;\n  min-width: 0;\n  width: var(--bmm-hp-side-width);\n}\n\n/* The second .combat-hp-side slot is reused by BossSession to render a\n   .gauntlet-section-header (e.g. \"ACE INHIBITORS\") when there's no boss\n   HP pill (Apothecarius / gauntlet flows). Restyle that wrapper so the\n   section name reads as a small section-context pill in the HUD strip,\n   not as a wide hp-pill-shaped slot wedged between Your HP and streak. */\n.bmm-content .combat-hp-side:has(.gauntlet-section-header) {\n  width: auto;\n  flex: 0 0 auto;\n  margin-left: 4px;\n}\n\n.bmm-content .gauntlet-section-header {\n  display: inline-flex;\n  align-items: center;\n  gap: 6px;\n  padding: 4px 10px;\n  height: 22px;\n  background: rgba(255, 255, 255, 0.04);\n  border: 1px solid rgba(255, 255, 255, 0.09);\n  border-radius: 8px;\n  white-space: nowrap;\n}\n\n.bmm-content .gauntlet-section-title {\n  font-size: 10px;\n  font-weight: 600;\n  letter-spacing: 0.12em;\n  text-transform: uppercase;\n  color: rgba(220, 220, 230, 0.78);\n  line-height: 1;\n}\n\n/* Inside the modal panel, the BLS-pill chrome from the dashboard rail\n   reads too transparent against the modal's glassy bg. Bump the surface\n   contrast so the pills actually look like chips, not floating text. */\n.bmm-content .hp-pill,\n.bmm-content .combat-streak {\n  background: rgba(255, 255, 255, 0.05);\n  border-color: rgba(255, 255, 255, 0.12);\n}\n\n/* Player HP is already on display in the BottomLeftStatusRail HUD —\n   showing it again inside the modal HUD is redundant noise. Hide it\n   AND collapse its hp-side wrapper so the streak chip + section pill\n   anchor cleanly without an empty slot. */\n.bmm-content .hp-pill--player {\n  display: none;\n}\n\n.bmm-content .combat-hp-side:has(.hp-pill--player) {\n  display: none;\n}\n\n.bmm-content .combat-streak--hot {\n  background: rgba(239, 68, 68, 0.08);\n  border-color: rgba(239, 68, 68, 0.22);\n}\n\n.bmm-content .combat-streak--blazing {\n  background: rgba(239, 68, 68, 0.14);\n  border-color: rgba(239, 68, 68, 0.32);\n}\n\n/* Status row holds the streak chip + survival-scale tag — slot it into\n   the same row as the HP pills, right-aligned via auto-margin so it\n   anchors at the trailing edge. */\n.bmm-content .combat-status-row {\n  flex: 0 0 auto;\n  margin: 0 0 0 auto;\n  gap: 6px;\n  align-items: center;\n}\n\n/* Hide BossSession's own back button — the modal's close X owns exit. */\n.bmm-content .combat-back {\n  display: none;\n}\n\n/* Hide the UserActionBar/exam-tabs chrome that BatchBossSession mounts\n   for the full-page route — the dashboard behind the modal already shows\n   the same upgrade / bug-report / settings actions, and duplicating them\n   inside the combat panel is redundant noise. */\n.bmm-content .exam-tabs-bar {\n  display: none;\n}\n\n/* Full-screen damage / heal flash overlays inside the modal would tint\n   the dashboard behind it. Scope the flash to the modal body. */\n.bmm-content .batch-self-damage-flash,\n.bmm-content .batch-heal-flash {\n  position: absolute;\n  inset: 0;\n  pointer-events: none;\n}\n\n@container (max-width: 1800px) {\n  .bmm-body {\n    --bmm-hud-close-reserve: clamp(48px, 3.2cqi, 56px);\n    --bmm-hud-height: clamp(64px, 4.1cqi, 72px);\n    --bmm-hp-side-width: clamp(176px, 12.5cqi, 220px);\n    --bmm-session-pad-x: clamp(16px, 1.35cqi, 24px);\n    --bmm-question-pad-x: clamp(1rem, 1.8cqi, 2rem);\n  }\n\n  .bmm-content .combat-hp-column {\n    gap: clamp(6px, 0.5cqi, 8px);\n  }\n\n  .bmm-content .combat-status-row {\n    gap: clamp(4px, 0.4cqi, 6px);\n  }\n}\n\n/* Fallback when portal target isn't a map viewport: become centered overlay. */\nbody > .bmm-overlay {\n  position: fixed;\n  padding: 40px;\n}\n\n@media (max-width: 640px) {\n  .bmm-overlay { padding: 16px; }\n  .bmm-content .batch-combat-header {\n    padding-right: 48px;\n  }\n}\n\n/* ── Athero (lipid) pool accent override ─────────────────────────────\n   Lipid mini-boss encounters (Chylo / Veldra / Idol / Eldra / Lipo-Alpha)\n   paint the modal in amber-gold instead of the default crimson combat\n   palette. Slug-to-pool mapping happens in App.jsx; this stylesheet\n   only handles the visual swap once data-pool=\"athero\" lands on the\n   .bmm-body element. Color anchor: #fbbf24 (matches PlayerLipidTag). */\n.bmm-body[data-pool=\"athero\"] {\n  border-color: rgba(251, 191, 36, 0.18);\n  box-shadow:\n    0 8px 32px rgba(0, 0, 0, 0.6),\n    0 0 0 1px rgba(251, 191, 36, 0.06) inset;\n}\n\n.bmm-body[data-pool=\"athero\"] .bmm-close {\n  background: rgba(251, 191, 36, 0.1);\n  border-color: rgba(251, 191, 36, 0.28);\n  color: rgba(252, 211, 77, 0.95);\n}\n.bmm-body[data-pool=\"athero\"] .bmm-close:hover {\n  background: rgba(251, 191, 36, 0.22);\n  border-color: rgba(251, 191, 36, 0.55);\n  color: #fff;\n}\n\n.bmm-body[data-pool=\"athero\"] .bmm-header {\n  border-bottom-color: rgba(251, 191, 36, 0.14);\n}\n\n.bmm-body[data-pool=\"athero\"] .bmm-avatar {\n  color: #fbbf24;\n  filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.45));\n}\n\n.bmm-body[data-pool=\"athero\"] .bmm-name {\n  color: #fef3c7;\n}\n\n.bmm-body[data-pool=\"athero\"] .bmm-content .batch-combat-header {\n  border-bottom-color: rgba(251, 191, 36, 0.12);\n}\n\n/* ── Anaphyl (MAB immunogenicity) pool accent override ───────────────\n   Murin-OMAB and later anaphyl enemies use amber-red: #fbbf24 warm\n   shoulder into #dc2626 core. Mirrors the athero override selectors. */\n.bmm-body[data-pool=\"anaphyl\"] {\n  border-color: rgba(220, 38, 38, 0.22);\n  box-shadow:\n    0 8px 32px rgba(0, 0, 0, 0.6),\n    0 0 0 1px rgba(251, 191, 36, 0.05) inset,\n    0 0 32px rgba(220, 38, 38, 0.08);\n}\n\n.bmm-body[data-pool=\"anaphyl\"] .bmm-close {\n  background: linear-gradient(135deg, rgba(251, 191, 36, 0.10), rgba(220, 38, 38, 0.12));\n  border-color: rgba(220, 38, 38, 0.34);\n  color: rgba(248, 113, 113, 0.96);\n}\n.bmm-body[data-pool=\"anaphyl\"] .bmm-close:hover {\n  background: linear-gradient(135deg, rgba(251, 191, 36, 0.18), rgba(220, 38, 38, 0.24));\n  border-color: rgba(220, 38, 38, 0.58);\n  color: #fff;\n}\n\n.bmm-body[data-pool=\"anaphyl\"] .bmm-header {\n  border-bottom-color: rgba(220, 38, 38, 0.16);\n}\n\n.bmm-body[data-pool=\"anaphyl\"] .bmm-avatar {\n  color: #dc2626;\n  filter: drop-shadow(0 0 8px rgba(220, 38, 38, 0.46));\n}\n\n.bmm-body[data-pool=\"anaphyl\"] .bmm-name {\n  color: #fee2e2;\n  text-shadow: 0 0 10px rgba(251, 191, 36, 0.16);\n}\n\n.bmm-body[data-pool=\"anaphyl\"] .bmm-content .batch-combat-header {\n  border-bottom-color: rgba(220, 38, 38, 0.14);\n}\n\n/* ── Dialysis pool accent override ───────────────────────────────────\n   Dialysis encounters reuse BatchBossSession with semantic inversion\n   (boss HP = toxin pool the player drains by answering correctly).\n   Cyan/teal palette to distinguish from combat-red. Anchor: #06b6d4\n   (cyan-500); deep stop #0891b2 (cyan-700). Mirrors the athero/anaphyl\n   selector structure. */\n.bmm-body[data-pool=\"dialysis\"] {\n  border-color: rgba(6, 182, 212, 0.18);\n  box-shadow:\n    0 8px 32px rgba(0, 0, 0, 0.6),\n    0 0 0 1px rgba(6, 182, 212, 0.06) inset;\n}\n\n.bmm-body[data-pool=\"dialysis\"] .bmm-close {\n  background: rgba(6, 182, 212, 0.1);\n  border-color: rgba(6, 182, 212, 0.28);\n  color: rgba(34, 211, 238, 0.95);\n}\n.bmm-body[data-pool=\"dialysis\"] .bmm-close:hover {\n  background: rgba(6, 182, 212, 0.22);\n  border-color: rgba(6, 182, 212, 0.55);\n  color: #fff;\n}\n\n.bmm-body[data-pool=\"dialysis\"] .bmm-content .batch-combat-header {\n  border-bottom-color: rgba(6, 182, 212, 0.14);\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
